import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { FunctionComponent } from 'react';
import { CompareQuotes } from '../../components/CompareQuotes/CompareQuotes';
import { StandaloneServicePlanForm } from '../../components/StandaloneServicePlan/StandaloneServicePlanForm';
import { getEnumValues, isTestStage } from '../../services/common/miscUtils';
import { saveAndContinueRehydrateSlice } from '../../services/redux/storeUtils';
import { CreditCheckStatusEnum } from '../../types/forms';
import { ProductFamilyEnum } from '../../types/productFamilySelection';
import { CreditCheckPage } from './CreditCheckPage/CreditCheckPage';
import { CustomerDataPage } from './CustomerDataPage/CustomerDataPage';
import { DocumentUploadPage } from './DocumentUploadPage/DocumentUploadPage';
import { AffordabilityDocumentUploadPage } from './AffordabilityDocumentUploadPage/AffordabilityDocumentUploadPage';
import { FinancialProductPage } from './FinancialProductPage/FinancialProductPage';
import { NewCarConfiguratorPage } from './NewCarConfiguratorPage/NewCarConfiguratorPage';
import { ProspectPage } from './ProspectPage/ProspectPage';
import { QuoteTypeSelectionPage } from './QuoteTypeSelectionPage/QuoteTypeSelectionPage';
import { UsedCarFormSelectorPage } from './UsedCarFormSelectorPage/UsedCarFormSelectorPage';

export enum WizardPagesEnum {
  'quoteTypeSelection' = 'quoteTypeSelection',
  'newCarConfigurator' = 'newCarConfigurator',
  'usedCarPage' = 'usedCarPage',
  'empty' = 'empty',
  'vehicle' = 'vehicle',
  'financialProduct' = 'financialProduct',
  'compareQuotes' = 'compareQuotes',
  'prospect' = 'prospect',
  'standaloneServicePlan' = 'standaloneServicePlan',
  'customerData' = 'customerData',
  'review' = 'review',
  'affordabilityDocuments' = 'affordabilityDocuments',
  'amlDocuments' = 'amlDocuments',
  'creditCheck' = 'creditCheck',
}

export enum WizardBreadcrumbEnum {
  Vehicle = 'vehicle',
  Quotation = 'quotation',
  Prospect = 'prospect',
  Application = 'application',
}

interface PageItem {
  /**
   * Translation key for breadcrumb text in the wizard.
   */
  breadcrumb: WizardBreadcrumbEnum;
  pageName: WizardPagesEnum;
  component: FunctionComponent;
  headingTranslationKey?: string;
}

export const newQuotationWizardPages: Array<PageItem> = [
  {
    breadcrumb: WizardBreadcrumbEnum.Vehicle,
    pageName: WizardPagesEnum.quoteTypeSelection,
    component: QuoteTypeSelectionPage,
  },
  {
    breadcrumb: WizardBreadcrumbEnum.Vehicle,
    pageName: WizardPagesEnum.newCarConfigurator,
    component: NewCarConfiguratorPage,
  },
  {
    breadcrumb: WizardBreadcrumbEnum.Vehicle,
    pageName: WizardPagesEnum.usedCarPage,
    component: UsedCarFormSelectorPage,
  },
  {
    breadcrumb: WizardBreadcrumbEnum.Quotation,
    pageName: WizardPagesEnum.financialProduct,
    component: FinancialProductPage,
  },
  {
    breadcrumb: WizardBreadcrumbEnum.Quotation,
    pageName: WizardPagesEnum.standaloneServicePlan,
    component: StandaloneServicePlanForm,
  },
  {
    breadcrumb: WizardBreadcrumbEnum.Quotation,
    pageName: WizardPagesEnum.compareQuotes,
    component: CompareQuotes,
  },
  {
    breadcrumb: WizardBreadcrumbEnum.Prospect,
    pageName: WizardPagesEnum.prospect,
    component: ProspectPage,
  },
  {
    breadcrumb: WizardBreadcrumbEnum.Application,
    pageName: WizardPagesEnum.customerData,
    component: CustomerDataPage,
  },
  {
    breadcrumb: WizardBreadcrumbEnum.Application,
    pageName: WizardPagesEnum.affordabilityDocuments,
    component: AffordabilityDocumentUploadPage,
  },
  {
    breadcrumb: WizardBreadcrumbEnum.Application,
    pageName: WizardPagesEnum.amlDocuments,
    component: DocumentUploadPage,
  },
  {
    breadcrumb: WizardBreadcrumbEnum.Application,
    pageName: WizardPagesEnum.creditCheck,
    component: CreditCheckPage,
  },
];

if (isTestStage) {
  /* eslint-disable no-console */
  console.groupCollapsed('Wizard sub-pages:');
  console.table(newQuotationWizardPages.map(({ pageName }) => ({ pageName })));
  console.groupEnd();
  console.groupCollapsed('Credit check statuses:');
  console.table(getEnumValues(CreditCheckStatusEnum));
  console.groupEnd();
  /* eslint-enable no-console */
}

export const newQuotationWizardBreadcrumbs: string[] = (() => {
  return newQuotationWizardPages.reduce((breadcrumbs, { breadcrumb }) => {
    if (!breadcrumbs.includes(breadcrumb)) {
      breadcrumbs.push(breadcrumb);
    }
    return [...breadcrumbs];
  }, [] as string[]);
})();

const getFirstPageForBreadcrumb = (breadcrumb: string): string | undefined => {
  // Find the first page that that is related to breadcrumb
  const page = newQuotationWizardPages.find((p) => p.breadcrumb === breadcrumb);
  return page?.pageName;
};

export const resolveWizardTargetPage = (
  breadcrumb: string,
  productFamily: ProductFamilyEnum | undefined
): WizardPagesEnum | undefined => {
  if (breadcrumb === WizardBreadcrumbEnum.Quotation) {
    return productFamily === ProductFamilyEnum.servicePlan
      ? WizardPagesEnum.standaloneServicePlan
      : WizardPagesEnum.financialProduct;
  }

  return getFirstPageForBreadcrumb(breadcrumb) as WizardPagesEnum | undefined;
};

interface NewQuotationState {
  wizard: {
    currentPageName?: WizardPagesEnum;
    navigateToCurrent: boolean;
    navigating: boolean;
  };
}

const initialState: NewQuotationState = {
  wizard: {
    currentPageName: undefined,
    navigateToCurrent: false,
    navigating: false,
  },
};

export const getWizardPageOfName = (pageName?: WizardPagesEnum) => {
  return newQuotationWizardPages.find(({ pageName: pageId }) => pageId === pageName) ?? newQuotationWizardPages[0];
};

const name = 'newQuotation';

export const slice = createSlice({
  name,
  initialState,
  selectors: {
    currentPageName: (state) => state.wizard.currentPageName,
    navigateToCurrent: (state) => state.wizard.navigateToCurrent,
    isNavigating: (state) => state.wizard.navigating,
  },
  reducers: {
    /**
     * - ⚠ Only for use in wizard component.
     * @inner
     */
    setCurrentPage: (state, action: PayloadAction<WizardPagesEnum>) => {
      state.wizard.currentPageName = action.payload;
    },
    wizardNavigateToPage: (state, action: PayloadAction<WizardPagesEnum>) => {
      state.wizard.currentPageName = action.payload;
      state.wizard.navigateToCurrent = true;
    },
    startNavigation: (state) => {
      state.wizard.navigating = true;
    },
    endNavigation: (state) => {
      state.wizard.navigating = false;
      state.wizard.navigateToCurrent = false;
    },
    wizardNextPage: (state) => {
      if (newQuotationWizardPages.at(-1)?.pageName === state.wizard.currentPageName) return;

      const currentIndex = newQuotationWizardPages.findIndex(
        ({ pageName }) => pageName === state.wizard.currentPageName
      );
      state.wizard.currentPageName = newQuotationWizardPages.at(currentIndex + 1)?.pageName;
      state.wizard.navigateToCurrent = true;
    },
    wizardPrevPage: (state) => {
      if (newQuotationWizardPages[0]?.pageName === state.wizard.currentPageName) return;

      const currentIndex = newQuotationWizardPages.findIndex(
        ({ pageName }) => pageName === state.wizard.currentPageName
      );
      state.wizard.currentPageName = newQuotationWizardPages.at(currentIndex - 1)?.pageName;
      state.wizard.navigateToCurrent = true;
    },
    wizardFinishNavigation: (state) => {
      state.wizard.navigateToCurrent = false;
    },
    resetNewQuotation: () => initialState,
  },
  extraReducers: (builder) => {
    saveAndContinueRehydrateSlice(builder, name);
  },
});

export const newQuotationSliceSelectors = slice.selectors;
export const newQuotationSliceActions = slice.actions;
export const newQuotationSlice = slice;
